import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Alert,
    Container,
    InputAdornment,
    Divider,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneAndroidRoundedIcon from "@mui/icons-material/PhoneAndroidRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PinRoundedIcon from "@mui/icons-material/PinRounded";
import {
    loginStart,
    requestOtp,
    verifyOtp,
    loginWithPassword,
    setCredentials,
    getOtpChannels,
    getMaintenanceStatus,
} from "../api";

/* Persist the session returned by verify-otp / login-password. */
function saveSession(data) {
    localStorage.setItem("accessToken", data.accessToken);
    localStorage.setItem("refreshToken", data.refreshToken);
    localStorage.setItem("userId", data.user.id);
    localStorage.setItem("firstName", data.user.firstName);
    localStorage.setItem("roleName", data.user.roleName);
    localStorage.setItem("roleLevel", data.user.roleLevel);
}

const onlyDigits = (v, max) => v.replace(/\D/g, "").slice(0, max);

export default function SignIn() {
    const navigate = useNavigate();
    const [channel, setChannel] = useState("sms");
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [mpin, setMpin] = useState("");
    const [otp, setOtp] = useState("");
    const [confirmMpin, setConfirmMpin] = useState("");
    // step: "identifier" | "credentials" | "verify" | "setup"
    const [step, setStep] = useState("identifier");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    /* Admin-controlled: which OTP channels this deployment offers. */
    const [otpChannels, setOtpChannels] = useState({ sms: true, email: true });
    /* Admin-controlled downtime window — warn before they try to sign in. */
    const [downtime, setDowntime] = useState(null);

    useEffect(() => {
        getOtpChannels()
            .then(({ data }) => {
                const cfg = { sms: !!data.sms, email: !!data.email };
                if (!cfg.sms && !cfg.email) return; // never leave both off
                setOtpChannels(cfg);
                /* If only one channel is on, that's the one they use. */
                if (cfg.sms !== cfg.email) setChannel(cfg.sms ? "sms" : "email");
            })
            .catch(() => {});
        getMaintenanceStatus()
            .then(({ data }) => setDowntime(data?.down ? data : null))
            .catch(() => {});
    }, []);

    const resetSecrets = () => {
        setPassword("");
        setMpin("");
        setConfirmMpin("");
        setOtp("");
    };

    /* Send an OTP and move to the verify step (setup + recovery path). */
    const startOtp = async () => {
        await requestOtp(identifier.trim(), channel);
        setNotice(`We sent a one-time code to ${identifier.trim()}.`);
        setStep("verify");
    };

    /* ------- step 1: identifier → decide password vs OTP-setup ------- */
    const handleContinue = async () => {
        setError("");
        setNotice("");
        if (!identifier.trim())
            return setError("Enter your registered phone or email");
        setLoading(true);
        try {
            const { data } = await loginStart(identifier.trim());
            if (data.needsSetup) {
                resetSecrets();
                await startOtp();
            } else {
                resetSecrets();
                setStep("credentials");
            }
        } catch (e) {
            setError(
                e.response?.data?.error || "Something went wrong. Try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    /* ------- step 2a: password + MPIN login (no SMS) ------- */
    const handlePasswordLogin = async () => {
        setError("");
        if (!password) return setError("Enter your password");
        if (!/^\d{4,6}$/.test(mpin)) return setError("Enter your 4–6 digit MPIN");
        setLoading(true);
        try {
            const { data } = await loginWithPassword(
                identifier.trim(),
                password,
                mpin,
            );
            saveSession(data);
            navigate("/dashboard");
        } catch (e) {
            const msg = e.response?.data?.error || "Invalid credentials";
            if (/set up/i.test(msg)) {
                setNotice("Let's set up your password & MPIN — verify with an OTP first.");
                resetSecrets();
                setLoading(true);
                try {
                    await startOtp();
                } catch (err) {
                    setError(err.response?.data?.error || "Failed to send OTP.");
                }
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    /* ------- step 2b: forgot → OTP recovery ------- */
    const handleForgot = async () => {
        setError("");
        setNotice("");
        setLoading(true);
        try {
            resetSecrets();
            await startOtp();
        } catch (e) {
            setError(e.response?.data?.error || "Failed to send OTP.");
        } finally {
            setLoading(false);
        }
    };

    /* ------- OTP verify ------- */
    const handleVerifyOtp = async () => {
        setError("");
        if (!otp.trim()) return setError("Please enter the OTP");
        setLoading(true);
        try {
            const { data } = await verifyOtp(identifier.trim(), otp.trim());
            saveSession(data);
            /* OTP is only ever used for first-time setup or forgot-password
               recovery — in BOTH cases the user must (re)set their password +
               MPIN. So always go to the setup step; never straight to the
               dashboard (that path skipped setup for users who already had
               creds but forgot them, leaving them unable to reset). */
            setNotice("Set your password and MPIN to finish signing in.");
            resetSecrets();
            setStep("setup");
        } catch (e) {
            setError(e.response?.data?.error || "Invalid OTP. Try again.");
        } finally {
            setLoading(false);
        }
    };

    /* ------- one-time credential setup (authenticated via OTP session) ------- */
    const handleSetup = async () => {
        setError("");
        if (password.length < 6)
            return setError("Password must be at least 6 characters");
        if (!/^\d{4,6}$/.test(mpin)) return setError("MPIN must be 4–6 digits");
        if (mpin !== confirmMpin) return setError("MPINs do not match");
        setLoading(true);
        try {
            await setCredentials(password, mpin);
            navigate("/dashboard");
        } catch (e) {
            setError(
                e.response?.data?.error || "Could not save credentials. Try again.",
            );
        } finally {
            setLoading(false);
        }
    };

    const backToStart = () => {
        setError("");
        setNotice("");
        resetSecrets();
        setStep("identifier");
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                    "linear-gradient(135deg, #14305a 0%, #2c5f9e 50%, #3b6fb0 100%)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: -150,
                    right: -100,
                    width: 450,
                    height: 450,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.05)",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: -100,
                    left: -80,
                    width: 350,
                    height: 350,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.04)",
                }}
            />

            <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        gap={1.5}
                        mb={1}
                    >
                        <Box
                            component="img"
                            src="/logo-icon.png"
                            alt="BiznessPay"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                            sx={{
                                height: 68,
                                width: 68,
                                objectFit: "contain",
                                bgcolor: "#fff",
                                borderRadius: 2,
                                p: 0.5,
                            }}
                        />
                        <Typography
                            variant="h5"
                            sx={{ color: "#fff", fontWeight: 800 }}
                        >
                            BiznessPay
                        </Typography>
                    </Stack>
                    <Typography
                        sx={{ color: "rgba(255,255,255,0.7)", fontSize: 15 }}
                    >
                        Sign in to your account
                    </Typography>
                </Box>

                <Card sx={{ p: 4, borderRadius: 4 }}>
                    {downtime && (
                        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                            {downtime.message} Sign-in reopens at{" "}
                            <strong>{downtime.end} IST</strong>.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {notice && !error && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            {notice}
                        </Alert>
                    )}

                    {/* -------------------- step 1: identifier -------------------- */}
                    {step === "identifier" && (
                        <Stack spacing={3}>
                            {/* Only show the switcher when the admin has both
                                channels on — with one, there's nothing to pick. */}
                            {otpChannels.sms && otpChannels.email && (
                                <ToggleButtonGroup
                                    value={channel}
                                    exclusive
                                    onChange={(_, v) => {
                                        if (v) {
                                            setChannel(v);
                                            setIdentifier("");
                                        }
                                    }}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        "& .MuiToggleButton-root": {
                                            borderRadius: 2,
                                            fontWeight: 600,
                                        },
                                    }}
                                >
                                    <ToggleButton value="sms">
                                        <PhoneAndroidRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Phone
                                    </ToggleButton>
                                    <ToggleButton value="email">
                                        <EmailRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                                        Email
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            <TextField
                                fullWidth
                                label={channel === "email" ? "Email Address" : "Phone Number"}
                                placeholder={
                                    channel === "email"
                                        ? "you@example.com"
                                        : "+91 9390616131"
                                }
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {channel === "email" ? (
                                                <EmailRoundedIcon
                                                    sx={{ color: "text.secondary", fontSize: 20 }}
                                                />
                                            ) : (
                                                <PhoneAndroidRoundedIcon
                                                    sx={{ color: "text.secondary", fontSize: 20 }}
                                                />
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                                onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleContinue}
                                disabled={loading}
                                sx={{ py: 1.5, borderRadius: 3, fontSize: 15 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Continue"
                                )}
                            </Button>
                        </Stack>
                    )}

                    {/* -------------------- step 2a: password + MPIN -------------------- */}
                    {step === "credentials" && (
                        <Stack spacing={2.5}>
                            <Typography color="text.secondary" fontSize={14} textAlign="center">
                                Signing in as <strong>{identifier}</strong>
                            </Typography>
                            <TextField
                                fullWidth
                                type="password"
                                label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockRoundedIcon
                                                sx={{ color: "text.secondary", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="MPIN"
                                placeholder="4–6 digits"
                                value={mpin}
                                onChange={(e) => setMpin(onlyDigits(e.target.value, 6))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PinRoundedIcon
                                                sx={{ color: "text.secondary", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                onKeyDown={(e) => e.key === "Enter" && handlePasswordLogin()}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handlePasswordLogin}
                                disabled={loading}
                                sx={{ py: 1.5, borderRadius: 3, fontSize: 15 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Sign In"
                                )}
                            </Button>
                            <Divider sx={{ my: 0.5 }} />
                            <Stack direction="row" justifyContent="space-between">
                                <Button variant="text" size="small" onClick={backToStart}>
                                    Change {channel === "email" ? "email" : "number"}
                                </Button>
                                <Button variant="text" size="small" onClick={handleForgot}>
                                    Forgot password / MPIN?
                                </Button>
                            </Stack>
                        </Stack>
                    )}

                    {/* -------------------- OTP verify -------------------- */}
                    {step === "verify" && (
                        <Stack spacing={3}>
                            <Typography color="text.secondary" fontSize={14} textAlign="center">
                                Enter the 6-digit code sent to <strong>{identifier}</strong>
                            </Typography>
                            <TextField
                                fullWidth
                                label="Enter OTP"
                                placeholder="000000"
                                value={otp}
                                onChange={(e) => setOtp(onlyDigits(e.target.value, 6))}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockRoundedIcon
                                                sx={{ color: "text.secondary", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    maxLength: 6,
                                    inputMode: "numeric",
                                    style: {
                                        letterSpacing: 8,
                                        fontWeight: 700,
                                        fontSize: 20,
                                        textAlign: "center",
                                    },
                                }}
                                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleVerifyOtp}
                                disabled={loading}
                                sx={{ py: 1.5, borderRadius: 3, fontSize: 15 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Verify"
                                )}
                            </Button>
                            <Button variant="text" size="small" onClick={backToStart}>
                                Change {channel === "email" ? "email" : "phone number"}
                            </Button>
                        </Stack>
                    )}

                    {/* -------------------- set password + MPIN -------------------- */}
                    {step === "setup" && (
                        <Stack spacing={2.5}>
                            <Typography color="text.secondary" fontSize={14} textAlign="center">
                                Create a password and MPIN. You'll use these to sign in from
                                now on — no OTP needed.
                            </Typography>
                            <TextField
                                fullWidth
                                type="password"
                                label="New Password"
                                placeholder="At least 6 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockRoundedIcon
                                                sx={{ color: "text.secondary", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="New MPIN"
                                placeholder="4–6 digits"
                                value={mpin}
                                onChange={(e) => setMpin(onlyDigits(e.target.value, 6))}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PinRoundedIcon
                                                sx={{ color: "text.secondary", fontSize: 20 }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                            />
                            <TextField
                                fullWidth
                                type="password"
                                label="Confirm MPIN"
                                value={confirmMpin}
                                onChange={(e) => setConfirmMpin(onlyDigits(e.target.value, 6))}
                                inputProps={{ inputMode: "numeric", maxLength: 6 }}
                                onKeyDown={(e) => e.key === "Enter" && handleSetup()}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={handleSetup}
                                disabled={loading}
                                sx={{ py: 1.5, borderRadius: 3, fontSize: 15 }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Save & Continue"
                                )}
                            </Button>
                        </Stack>
                    )}
                </Card>

                <Typography
                    sx={{
                        textAlign: "center",
                        mt: 3,
                        color: "rgba(255,255,255,0.6)",
                        fontSize: 13,
                    }}
                >
                    Don't have an account? Ask your upline for an invite.
                </Typography>
            </Container>
        </Box>
    );
}
